// dependencies
import { Column, GridApi, ManagedGridOptionKey, ProcessUnpinnedColumnsParams } from "ag-grid-community";
import { themeAlpine } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { faChartScatterBubble, faClipboardList, faTableColumns } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import Button from "../../../../../../components/Button";
import ColumnPicker, { DataValue } from "../../../../../../components/ColumnPicker";
import GridWithToolPanel from "../../../../../../components/GridWithToolPanel";
import OptionsDrawer from "../../../../../../components/OptionsDrawer";
import {
	booleanCellRenderer,
	currentGoalProgressCellRenderer,
	dateCellRenderer,
	determineCellRenderer,
	goalCategoriesCellRenderer,
	goalCellRenderer,
	goalNameCellRenderer,
	goalOutlookCellRenderer,
	goalStatusCellRenderer,
	goalTargetCellRenderer,
	goalTransparencyCellRenderer,
	groupCellRenderer,
	numericCellRenderer,
	peopleCellRenderer,
	percentageCellRenderer,
	personCellRenderer,
	reviewCycleCellRenderer,
	reviewCycleStatusCellRenderer,
	reviewFormNameCellRenderer,
	reviewReportStatusCellRenderer,
	reviewTemplateCellRenderer,
	superRow,
	superRowCellRenderer,
	surveyNameCellRenderer,
} from "./CellRenderers";
import ColumnHeader from "./ColumnHeader";
import SelectSummaryFunction from "./SelectSummaryFunction";

import ErrorPage from "../../../../../../pages/Error";

import { FieldFilter, FilterComparison, SummaryFunction } from "../../../../schemas/report";

// store
import useReportStore from "../../store";
import { ChartType as Chart, nineBoxRequiredFields } from "../../store/enums";

import { useRbacPrimaryRolePermissions } from "../../../../../../../react/api/hooks/defs/rbac";
import { useCompanyFunction, useCompanySettings } from "../../../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";
import { useGetGridDataCount, useGetReportGridData } from "../../../../api/hooks";

import get from "../../../../../../utils/GetNested";
import {
	formatSummarizeByAndAggregationOperations,
	getFieldTypeFromSummnarizeByFieldValue,
	getReportTypeSpecificPermissions,
	getUserSpecificCompanySettingDependentPermissions,
} from "../../utils";
// utils
import { recalculateColumnVisibility, selectCellRendererType, superRowConfig } from "./utils";

import { CustomNoRowsOverlay } from "../../../../AgGridCustomOverlay/customNoRowsOverlay";
import SummarizeRows from "../SummarizeRows";
import VisualizePanel from "../VisualizePanel";
import * as ReportingChart from "../VisualizePanel/ReportingCharts";
import css from "./ReportTable.module.scss";

// schemas
// styles

// Custom theme for ReportTable - exported for use in stories and other components
export const ReportTableTheme = themeAlpine.withParams({
	// spacing
	rowHeight: 40,
	headerHeight: 50,
	borderRadius: 4,
	cellHorizontalPadding: 12,

	// typography
	fontFamily: "Source Sans Pro, sans-serif",
	fontSize: "14px",

	// colors - using CSS variables for PerformYard theme integration
	backgroundColor: "var(--py-neutral-0)",
	headerBackgroundColor: "var(--py-neutral-1)",
	borderColor: "var(--py-neutral-2)",
	rowHoverColor: "transparent",
});

const chartMap = {
	[Chart.BarChart]: <ReportingChart.BarChart />,
	[Chart.ColumnChart]: <ReportingChart.ColumnChart />,
	[Chart.PieChart]: <div>Pie Chart</div>,
	[Chart.ScatterChart]: <ReportingChart.ScatterChart />,
	[Chart.HistogramChart]: <ReportingChart.HistogramChart />,
	[Chart.NineBox]: <ReportingChart.NineBoxChart />,
	[Chart.TrendlineChart]: <ReportingChart.TrendlineChart />,
};

const MIN_SCROLLABLE_WIDTH = 100;

interface BaseProps {
	decimalPlaces: number;
	pageNumber: number;
	pageSize: number;
	openColumnPicker: boolean;
	updatePagination: ({ pageNumber, pageSize }: { pageNumber: number; pageSize: number }) => void;
}

const ReportTable = (props: BaseProps) => {
	const { pageSize, openColumnPicker, updatePagination, decimalPlaces } = props;

	const { companyFunction: companyReportingVisualizationFunction } = useCompanyFunction("reporting-visualization");
	const { data: companySettings } = useCompanySettings();
	const companyTimezone = companySettings?.default_timezone || "UTC";
	const { value: ffReportingVisualization } = useFeatureFlagQuery("ff-is-reporting-visualizations-enabled", true);
	const { value: ffSurveysEnabled } = useFeatureFlagQuery("ff-is-surveys-enabled", false);
	const { data: permissions } = useRbacPrimaryRolePermissions("report_analytics");

	const { managerPermissions, contributorPermissions, groupAdminPermissions } =
		getUserSpecificCompanySettingDependentPermissions();

	const hasManagerAccess =
		managerPermissions.canViewGoalReports ||
		managerPermissions.canViewReviewReports ||
		managerPermissions.canViewEmployeeReports;
	const hasContributorAccess =
		contributorPermissions.canViewGoalReports ||
		contributorPermissions.canViewReviewReports ||
		contributorPermissions.canViewEmployeeReports;
	const hasGroupAdminAccess = Object.values(groupAdminPermissions)?.some(Boolean);

	const { permissionToCreateReviewReport, permissionToCreateEmployeeReport } =
		getReportTypeSpecificPermissions(permissions);

	const permissionToViewSharedWithReports = permissions?.length
		? hasManagerAccess || hasContributorAccess || hasGroupAdminAccess
		: undefined;

	const gridRef = useRef<AgGridReact<any>>(null);
	// Track if grid has completed initial load to prevent unmount/remount loops during refetches
	const hasInitiallyLoaded = useRef(false);
	const [gridViewportWidth, setGridViewportWidth] = useState<number>(Infinity);
	const observerRef = useRef<ResizeObserver | null>(null);

	// Callback ref: attaches ResizeObserver when the grid container div mounts
	const gridContainerRef = useCallback((node: HTMLDivElement | null) => {
		if (observerRef.current) {
			observerRef.current.disconnect();
			observerRef.current = null;
		}
		if (node) {
			const observer = new ResizeObserver((entries) => {
				for (const entry of entries) {
					setGridViewportWidth(entry.contentRect.width);
				}
			});
			observer.observe(node);
			observerRef.current = observer;
		}
	}, []);

	const [
		report,
		enabledGridColumns,
		getEnabledGridColumns,
		setEnabledGridColumns,
		setColumnMetaData,
		toggleGridColumn,
		getAvailableGridColumns,
		setActiveModal,
		ungroupGroupedQuestion,
		setGridDataCount,
		getGridSorts,
		setSorts,
		isSummarizeRowsView,
		setSummaryFunctions,
		summaryFunctions,
		getSummaryColumnMetadata,
		showAllAggregationColumns,
		getEnabledSummarizeRowsColumns,
		enabledSummarizeFields,
		setEnabledSummarizeFields,
		loadQuestionGroup,
		selectedChart,
		getAvailableCharts,
		fallbackToTableChart,
		visualizeOptions,
		setVisualizeOptions,
		canViewSummary,
		expandedSuperRowCell,
		getExpandedSuperRowCell,
		error,
		validateAndFilterSorts,
		toggleColumnPin,
		sorts,
		getAppliedFilters,
		pagination,
		fetchCanViewSummary,
		searchTerm,
		columnMetadata,
	] = useReportStore((state) => [
		state.report,
		state.enabledGridColumns,
		state.getEnabledGridColumns,
		state.setEnabledGridColumns,
		state.setColumnMetaData,
		state.toggleGridColumn,
		state.getAvailableGridColumns,
		state.setActiveModal,
		state.ungroupGroupedQuestion,
		state.setGridDataCount,
		state.getGridSorts,
		state.setSorts,
		state.isSummarizeRowsView,
		state.setSummaryFunctions,
		state.summaryFunctions,
		state.getSummaryColumnMetadata,
		state.showAllAggregationColumns,
		state.getEnabledSummarizeRowsColumns,
		state.enabledSummarizeFields,
		state.setEnabledSummarizeFields,
		state.questionGroupingModal.loadQuestionGroup,
		state.selectedChart,
		state.getAvailableCharts,
		state.fallbackToTableChart,
		state.visualizeOptions,
		state.setVisualizeOptions,
		state.canViewSummary,
		state.expandedSuperRowCell,
		state.getExpandedSuperRowCell,
		state.error,
		state.validateAndFilterSorts,
		state.toggleColumnPin,
		state.sorts,
		state.getAppliedFilters,
		state.pagination,
		state.fetchCanViewSummary,
		state.searchTerm,
		state.columnMetadata,
	]);
	useEffect(() => {
		fetchCanViewSummary();
	}, [report]);

	useEffect(() => {
		if (!isSummarizeRowsView || canViewSummary === false) {
			fallbackToTableChart();
		}
	}, [isSummarizeRowsView, canViewSummary]);

	const columns = getEnabledGridColumns().map((column) => {
		const type = column.fieldType;
		const columnID = type === "EMPLOYEE_FIELD" ? column.headerName : column.field;
		return { type, id: columnID, fieldValueType: column.fieldValueType };
	});
	const { summarizeBy, aggregationOperations } = useMemo(() => {
		if (!isSummarizeRowsView || !canViewSummary) {
			return { summarizeBy: [], aggregationOperations: [] };
		}
		return formatSummarizeByAndAggregationOperations(enabledSummarizeFields, getSummaryColumnMetadata());
	}, [isSummarizeRowsView, canViewSummary, enabledSummarizeFields, summaryFunctions, columns]);

	const baseFilters = getAppliedFilters();
	const filters = searchTerm
		? [
				...baseFilters,
				[
					{
						field: "",
						type: "search",
						comparison: FilterComparison.Contains,
						value: searchTerm,
						category: "EMPLOYEE_FIELD" as FieldFilter["category"],
					},
				],
			]
		: baseFilters;

	const countVars = {
		columns,
		filters,
		sorts,
		summarizeBy,
		aggregationOperations,
		...(report?.type === "reviewReport" || report?.type === "surveyReport"
			? { questionGroupingSessionID: report?.questionGroupingSessionID || "" }
			: {}),
	};

	const queryVars = {
		...countVars,
		pageNumber: pagination?.pageNumber || 1,
		pageSize: pageSize || 50,
	};
	// Don't fetch until columns are ready (prevents caching an empty response on initial load)
	// and until canViewSummary has been determined for summarize views
	const isQueryEnabled = columns.length > 0 && !(isSummarizeRowsView && canViewSummary === undefined);
	const gridResult = useGetReportGridData([queryVars], report?.type, isQueryEnabled)?.[0];
	const gridData = (gridResult?.data ?? []) as any[];

	const isLoading = gridResult?.isLoading ?? false;
	const isFetching = gridResult?.isFetching ?? false;

	const { data: countData } = useGetGridDataCount(countVars, report?.type, isQueryEnabled);
	const gridDataCount = countData?.reviewReportsCount?.count ?? 0;

	useEffect(() => {
		setGridDataCount(gridDataCount);
	}, [gridDataCount]);

	// Calculate summarized columns
	const summarizeCols = useMemo(() => {
		return isSummarizeRowsView && canViewSummary ? getEnabledSummarizeRowsColumns() : [];
	}, [isSummarizeRowsView, canViewSummary, enabledSummarizeFields]);

	// Estimated pinned width from state (used for canPin check during render)
	const totalPinnedWidth = useMemo(() => {
		let width = 0;
		for (const col of summarizeCols) {
			width += columnMetadata[col.field]?.width ?? col.width ?? 200;
		}
		for (const field of enabledGridColumns) {
			if (columnMetadata[field]?.pinned === "left") {
				width += columnMetadata[field]?.width ?? 200;
			}
		}
		return width;
	}, [columnMetadata, enabledGridColumns, summarizeCols]);

	// Unpin rightmost user-pinned columns when pinned area exceeds available space.
	// Called from the reactive useEffect AND after recalculateColumnVisibility.
	const unpinColumnsIfOverflowing = useCallback(() => {
		if (!isFinite(gridViewportWidth)) return;
		const api = gridRef.current?.api;
		const pinnedWidth = api
			? api.getDisplayedLeftColumns().reduce((sum, col) => sum + col.getActualWidth(), 0)
			: totalPinnedWidth;
		if (pinnedWidth + MIN_SCROLLABLE_WIDTH <= gridViewportWidth) return;

		const currentMeta = columnMetadataRef.current;
		const userPinnedFields = enabledGridColumns.filter((field) => currentMeta[field]?.pinned === "left");
		if (userPinnedFields.length === 0) return;

		const updatedMeta = { ...currentMeta };
		let currentPinnedWidth = pinnedWidth;
		for (let i = userPinnedFields.length - 1; i >= 0; i--) {
			if (currentPinnedWidth + MIN_SCROLLABLE_WIDTH <= gridViewportWidth) break;
			const field = userPinnedFields[i];
			const colWidth = updatedMeta[field]?.width ?? 200;
			updatedMeta[field] = { ...updatedMeta[field], pinned: null };
			currentPinnedWidth -= colWidth;
		}
		setColumnMetaData(updatedMeta);
	}, [gridViewportWidth, totalPinnedWidth, enabledGridColumns]);

	// Reactive safety net: re-check on viewport resize or pinned width changes
	useEffect(() => {
		unpinColumnsIfOverflowing();
	}, [gridViewportWidth, totalPinnedWidth]);

	if (!permissions?.length) {
		return null;
	}

	const adminPermissions =
		(permissionToCreateEmployeeReport && report?.type === "employeeReport") || permissionToCreateReviewReport;

	if (
		error ||
		(!adminPermissions &&
			hasManagerAccess === false &&
			hasContributorAccess === false &&
			hasGroupAdminAccess === false) ||
		(!ffSurveysEnabled && report?.type === "surveyReport")
	) {
		return <ErrorPage code={403} />;
	}
	const allSummaryFunctions = getSummaryColumnMetadata();

	const availableCharts = getAvailableCharts();
	// Use a stable string key to prevent infinite re-render loops when filters return empty results.
	// getAvailableCharts() creates a new array reference on each call, which would cause the
	// useEffect to fire on every render even when the actual chart list hasn't changed.
	const availableChartsKey = availableCharts.join(",");

	useEffect(() => {
		if (!availableCharts.some((chart) => chart === selectedChart) && !isLoading) {
			fallbackToTableChart();
		}
	}, [availableChartsKey]);

	useEffect(() => {
		if (companyReportingVisualizationFunction !== "enabled" || !ffReportingVisualization) {
			fallbackToTableChart();
		}
	}, [selectedChart, companyReportingVisualizationFunction, ffReportingVisualization]);

	useEffect(() => {
		// when the "loading" option is true, displays the loading overlay
		const agGridLoadingKey = "loading" as ManagedGridOptionKey;
		if (gridRef.current && gridRef.current.api) {
			gridRef.current.api.setGridOption(agGridLoadingKey, isFetching);
			if (!isFetching) {
				onGridReady({ api: gridRef.current.api }); // reset column state after fetch
			}
		}
	}, [isFetching]);

	useEffect(() => {
		if (gridRef.current && gridRef.current.api) {
			if (isSummarizeRowsView && canViewSummary) {
				gridRef.current.api.setGridOption("headerHeight", 85);
			} else {
				gridRef.current.api.setGridOption("headerHeight", 50);
			}
		}
	}, [isSummarizeRowsView, canViewSummary]);

	// Safety net: when pinned columns exceed viewport, ag-grid calls this to decide
	// which columns to unpin. We keep summarize columns pinned and only unpin user-pinned.
	const columnMetadataRef = useRef(columnMetadata);
	useEffect(() => {
		columnMetadataRef.current = columnMetadata;
	}, [columnMetadata]);

	const processUnpinnedColumns = useRef((params: ProcessUnpinnedColumnsParams) => {
		const { columns } = params;
		const userPinnedCols: Column[] = [];
		for (const col of columns) {
			const colDef = col.getUserProvidedColDef();
			if (colDef?.lockPosition === "left") {
				continue;
			}
			userPinnedCols.push(col);
		}
		if (userPinnedCols.length > 0) {
			const fieldsToUnpin = userPinnedCols.map((col) => col.getColId());
			queueMicrotask(() => {
				const currentMeta = columnMetadataRef.current;
				const updatedMeta = { ...currentMeta };
				for (const field of fieldsToUnpin) {
					if (updatedMeta[field]) {
						updatedMeta[field] = { ...updatedMeta[field], pinned: null };
					}
				}
				setColumnMetaData(updatedMeta);
			});
		}
		return userPinnedCols;
	}).current;

	// set initial column sort on load
	const onGridReady = ({ api: gridApi }: { api: GridApi }) => {
		const gridSorts = getGridSorts();
		const sortsToValidate = gridSorts
			.filter((sort) => sort.sort)
			.map((sort) => ({ ...sort, direction: sort.sort as "asc" | "desc" }));
		const validSorts = validateAndFilterSorts(sortsToValidate);
		const columnStateSorts = validSorts.map((sort) => ({ ...sort, sort: sort.direction }));
		gridApi.applyColumnState({
			state: columnStateSorts,
			defaultState: { sort: null },
		});
		if (validSorts.length !== sortsToValidate.length) {
			updatePagination({ pageNumber: 1, pageSize });
			setSorts(validSorts);
		}
		if (isSummarizeRowsView && canViewSummary) {
			gridApi.setGridOption("headerHeight", 85);
			recalculateColumnVisibility(gridApi, getSummaryColumnMetadata);
		}
		if (gridData?.length === 0 && !isLoading) {
			gridApi.showNoRowsOverlay();
		}
	};

	useEffect(() => {
		if (gridRef.current && gridRef.current.api) {
			recalculateColumnVisibility(gridRef.current.api, getSummaryColumnMetadata);
			unpinColumnsIfOverflowing();
		}
	}, [canViewSummary, showAllAggregationColumns, isSummarizeRowsView, enabledSummarizeFields?.length]);

	// Wait for canViewSummary to be determined and initial data to load before rendering
	// Only return null on initial load - don't unmount grid during refetches (causes infinite loop with empty results)
	if (canViewSummary === undefined || (!hasInitiallyLoaded.current && gridData?.length === 0 && isLoading)) {
		return null;
	}

	// Mark initial load as complete once we render the grid
	if (!hasInitiallyLoaded.current) {
		hasInitiallyLoaded.current = true;
	}

	const reorderSorts = (sorts) => {
		return sorts
			.filter((item) => item.sort !== null)
			.sort((a, b) => {
				return a.sortIndex - b.sortIndex;
			})
			.map((col) => {
				return {
					colId: col.colId,
					direction: col.sort,
				};
			});
	};

	// on sort, save the sort state locally and update the grid data
	// Sort is always driven by the custom header components (ColumnHeader / SelectSummaryFunction)
	// which pass sort objects directly. ag-grid's onColumnHeaderClicked is disabled.
	const handleSort = (sorts) => {
		const orderedSortsToValidate: { colId: string; direction: "asc" | "desc" }[] = reorderSorts(sorts);

		const validatedSorts = validateAndFilterSorts(orderedSortsToValidate);

		gridRef.current?.api.applyColumnState({
			state: validatedSorts.map((sort) => ({ ...sort, sort: sort.direction })),
			defaultState: {
				sort: null,
			},
		});

		// handle sorts in summary view
		if (isSummarizeRowsView) {
			// when reordering sorts in summarize rows tab, we want to make sure the last sort is the one that is visualized
			if (validatedSorts.length > 0) {
				const existingVisualizeOptions = visualizeOptions[selectedChart] || {};
				// filter to make sure dataSeries is summarized by field
				let orderedSortsToVisualize = [...validatedSorts];
				if (selectedChart === Chart.NineBox) {
					orderedSortsToVisualize = orderedSortsToVisualize.filter((option) =>
						nineBoxRequiredFields.has(option.colId.toLowerCase())
					);
				}
				// if it's a trendline chart, only allow date fields
				if (selectedChart === Chart.TrendlineChart) {
					orderedSortsToVisualize = orderedSortsToVisualize.filter((option) => {
						const field = option.colId.replace(/^summary_/, "");
						const fieldType = getFieldTypeFromSummnarizeByFieldValue(field);
						return fieldType === "date";
					});
				}
				// Update the data series to the last sort only if it's not currently in table view.
				if (orderedSortsToVisualize.length > 0 && selectedChart !== Chart.Table) {
					setVisualizeOptions({
						[selectedChart]: {
							...existingVisualizeOptions,
							dataSeries: {
								...(existingVisualizeOptions.dataSeries || {}),
								column: orderedSortsToVisualize[orderedSortsToVisualize.length - 1].colId,
							},
						},
					});
				}
			}
		}
		updatePagination({ pageNumber: 1, pageSize: pageSize });
		setSorts(validatedSorts);
	};

	const summarySelectionChanged = (aggregationValue, columnInfo) => {
		if (columnInfo.field.split("_")[0] === "summary") {
			// handle secondary selection for summarize rows columns
			const updatedEnabledSummarizeFields = [...enabledSummarizeFields];
			const index = updatedEnabledSummarizeFields.findIndex(
				(item) => item.value === columnInfo.field.split("summary_")[1]
			);
			updatedEnabledSummarizeFields[index].selection = aggregationValue.value;
			setEnabledSummarizeFields(updatedEnabledSummarizeFields);
		} else {
			// handle aggregation operations for non summarize rows columns
			const updatedSummaryFunctions = { ...summaryFunctions };
			updatedSummaryFunctions[columnInfo.field] = aggregationValue.value;
			setSummaryFunctions(updatedSummaryFunctions);
		}
	};

	// we add a few more properties to the grid columns after we get them from
	// the store
	const augmentedGridColumns = getEnabledGridColumns().map((col) => {
		const { columnMetadata, ...column } = col;
		const allSorts = getGridSorts();
		const { currentlySelectedSummaryFunction, availableSummaryFunctions } = allSummaryFunctions[column.field];
		const isGroupedQuestion = column.fieldType === "GROUPED_QUESTION";

		let cellRenderer = selectCellRendererType({
			type: col.fieldValueType || "",
			isSummarizeByField: false,
			isSummaryReport: isSummarizeRowsView,
			isGroupedQuestion,
		});

		if (isSummarizeRowsView && canViewSummary) {
			const summaryFunction = allSummaryFunctions[column.field].currentlySelectedSummaryFunction;
			if (summaryFunction === SummaryFunction.Count) {
				cellRenderer = "countCellRenderer";
			}
		}

		const columnSort = getGridSorts().find((sort) => sort.colId === col.field) || null;

		const colWidth = columnMetadata?.width ?? 200;
		const isPinned = columnMetadata?.pinned === "left";
		const canPin = isPinned || gridViewportWidth - totalPinnedWidth - colWidth >= MIN_SCROLLABLE_WIDTH;

		return {
			cellClass: "ag-grid-cell",
			headerClass: `ag-grid-header ${
				isSummarizeRowsView && canViewSummary ? "summarize-header" : null
			} no-override-tooltip`,
			cellRenderer,
			cellRendererParams: {
				decimalPlaces,
			},
			...column,
			...columnMetadata,
			lockPinned: true,
			resizable: true,
			headerTooltip: column.headerName,
			headerComponent: "ColumnHeader",
			headerComponentParams: {
				isSummarizeRowsView: isSummarizeRowsView,
				isSummarizeByColumn: false,
				onInputChange: summarySelectionChanged,
				selectedColumn: col,
				summaryFunction: currentlySelectedSummaryFunction,
				availableSummaryFunctions,
				allSorts,
				columnSort,
				handleSort: handleSort,
				pinned: columnMetadata?.pinned || null,
				onPinToggle: toggleColumnPin,
				canPin,
			},
		};
	});

	// similar to augemntedGridColumns above,
	// we need to add more properties to the summarize grid columns
	let summarizeRowColumns = getEnabledSummarizeRowsColumns();

	if (isSummarizeRowsView && canViewSummary) {
		const rowSpan = {};
		const duplicateRows = {};

		// finds the correct row span info from the previous column, if it exists
		const getPrevColRowSpanInfo = (prevCol, rowIndex) => {
			const prevColSpans = Object.keys(rowSpan).filter((summaryFieldAndIndex) => {
				return summaryFieldAndIndex.includes(prevCol);
			});

			for (let i = 0; i < prevColSpans.length; i++) {
				const prevColSpan = prevColSpans[i];
				const spanRowIndex = parseInt(prevColSpan.split("_").pop() || "");

				if (rowIndex < spanRowIndex + rowSpan[prevColSpan] && rowIndex >= spanRowIndex) {
					return {
						prevColRowSpan: rowSpan[prevColSpan],
						prevColRowIndex: spanRowIndex,
					};
				}
			}

			return null;
		};

		// this function determines if row span should be added to this cell
		const getShouldAddRowSpan = (fieldIndex, nextRowIndex, rowsSpanned, prevColRowSpanInfo) => {
			// for the first column, we always add row span
			// since we don't have a previous column to compare to
			if (fieldIndex === 0) {
				return true;
			}

			if (prevColRowSpanInfo) {
				const { prevColRowSpan, prevColRowIndex } = prevColRowSpanInfo;
				// if total rows spanned so far for this span group is less than the previous column's row span group
				// AND the next row index doesn't exceed the span amount from the previous column's row index,
				// then we should add row span to this cell
				if (rowsSpanned < prevColRowSpan && nextRowIndex < prevColRowSpan + prevColRowIndex) {
					return true;
				}
			}

			return false;
		};
		// get all the summarizeBy field columns
		const summaryFields = gridData && gridData[0]?._id ? Object.keys(gridData[0]._id) : [];

		// Helper to check if all _id fields match between two rows
		// This ensures rows are only spanned when they represent the exact same group
		// implemented in PY-11173 to ensure we can separate current author summary groups by redacted/non redacted forms
		const allIdFieldsMatch = (id1: Record<string, unknown>, id2: Record<string, unknown>) => {
			const keys = Object.keys(id1);
			return keys.every((key) => id1[key] === id2[key]);
		};

		// main function to determine row spanning:
		// 1). iterate through each summaryField: ex ['EMPLOYEE_FIELD_manager', 'REVIEW_FIELD_subject']
		// 2). iterate iterate through each row of data
		// 3). As long as the row isn't already a part of a row span:
		//     - while the next row cell has the same value as the current row cell,
		//     - check if number of rows spanned has exceeded the row span of the previous column
		//     - if not, add to row span and add the row to duplicateRows so we don't check the row again later on
		summaryFields.forEach((field, fieldIndex) => {
			gridData.forEach((row, rowIndex) => {
				if (!duplicateRows[`${field}_${rowIndex}`]) {
					let nextRowIndex = rowIndex + 1;
					const currentRowValue = row._id[field];
					const prevColSpanInfo = summaryFields[fieldIndex - 1]
						? getPrevColRowSpanInfo(summaryFields[fieldIndex - 1], rowIndex)
						: null;
					while (
						gridData[nextRowIndex] &&
						gridData[nextRowIndex]._id[field] === currentRowValue &&
						(field !== "current_author" || allIdFieldsMatch(row._id, gridData[nextRowIndex]._id))
					) {
						const rowsSpanned = rowSpan[`${field}_${rowIndex}`] || 1;

						const shouldAddRowSpan = getShouldAddRowSpan(
							fieldIndex,
							nextRowIndex,
							rowsSpanned,
							prevColSpanInfo
						);

						if (shouldAddRowSpan) {
							rowSpan[`${field}_${rowIndex}`] = rowsSpanned + 1;
							duplicateRows[`${field}_${nextRowIndex}`] = true;
						}
						nextRowIndex++;
					}
				}
			});
		});

		summarizeRowColumns = summarizeRowColumns.map((col, index) => {
			const { ...column } = col;

			// if row is part of row span, add row span stying
			const cellRules = {
				"cell-span": (params) => {
					const { colDef, node } = params;
					const field = colDef.fieldPath.slice(1).slice(0, -1).join("_");

					// Get the current expanded super row to calculate row index shifts
					const expandedSuperRowCell = getExpandedSuperRowCell();

					// Only adjust row index if there's an expanded super row
					if (expandedSuperRowCell) {
						const getRowIndexShift = (originalRowIndex: number) => {
							// If the super row is inserted before this row, shift by 1
							return expandedSuperRowCell.row < originalRowIndex ? 1 : 0;
						};

						// Adjust the row index based on super row expansion
						const adjustedRowIndex = node.rowIndex - getRowIndexShift(node.rowIndex);

						if (
							rowSpan[`${field}_${adjustedRowIndex}`] > 1 ||
							duplicateRows[`${field}_${adjustedRowIndex}`]
						) {
							return true;
						}
					} else {
						// No super row expanded, use original row index
						if (rowSpan[`${field}_${node.rowIndex}`] > 1 || duplicateRows[`${field}_${node.rowIndex}`]) {
							return true;
						}
					}
				},
				// Additional class to find the first row of the cell-span
				// in order to toggle it's bottom border along with any super row opening in front of it
				"first-cell-span": (params) => {
					const { colDef, node } = params;
					const field = colDef.fieldPath.slice(1).slice(0, -1).join("_");

					// Get the current expanded super row to calculate row index shifts
					const expandedSuperRowCell = getExpandedSuperRowCell();

					// Only adjust row index if there's an expanded super row
					if (expandedSuperRowCell) {
						const getRowIndexShift = (originalRowIndex: number) => {
							// If the super row is inserted before this row, shift by 1
							return expandedSuperRowCell.row < originalRowIndex ? 1 : 0;
						};

						// Adjust the row index based on super row expansion
						const adjustedRowIndex = node.rowIndex - getRowIndexShift(node.rowIndex);

						if (rowSpan[`${field}_${adjustedRowIndex}`] > 1) {
							return true;
						}
					} else {
						// No super row expanded, use original row index
						if (rowSpan[`${field}_${node.rowIndex}`] > 1) {
							return true;
						}
					}
				},
			};

			const columnField = column.field;
			const columnSort = getGridSorts().find((sort) => sort.colId === columnField) || null;
			const currentlySelectedSummaryFunction = column.selection;
			const availableSummaryFunctions = column.selectionOptions;

			return {
				...column,
				rowSpan: (params) => {
					const { colDef, node } = params;
					// remove SummairzeBy and name from field path to get the field name
					const field = colDef.fieldPath.slice(1).slice(0, -1).join("_");

					// Get the current expanded super row to calculate row index shifts
					const expandedSuperRowCell = getExpandedSuperRowCell();

					// Only adjust row index if there's an expanded super row
					if (expandedSuperRowCell) {
						const getRowIndexShift = (originalRowIndex: number) => {
							// If the super row is inserted before this row, shift by 1
							return expandedSuperRowCell.row < originalRowIndex ? 1 : 0;
						};

						// Adjust the row index based on super row expansion
						const adjustedRowIndex = node.rowIndex - getRowIndexShift(node.rowIndex);

						return rowSpan[`${field}_${adjustedRowIndex}`] || 1;
					} else {
						// No super row expanded, use original row index
						return rowSpan[`${field}_${node.rowIndex}`] || 1;
					}
				},
				cellClassRules: cellRules,
				cellClass: `ag-grid-cell ${index === summarizeRowColumns.length - 1 ? "last-summarize-column" : null}`,
				headerClass: `ag-grid-header no-override-tooltip ${
					index === summarizeRowColumns.length - 1 ? "last-summarize-column" : null
				}`,
				pinned: "left",
				resizable: true,
				cellRenderer: selectCellRendererType({
					type: column.fieldValueType || "",
					isSummarizeByField: true,
					isSummaryReport: isSummarizeRowsView,
					isGroupedQuestion: false, // cannot summrize by grouped questions
				}),
				cellRendererParams: {
					currentlySelectedSummaryFunction,
				},
				headerComponent: isSummarizeRowsView ? "SummaryFunction" : null,
				headerComponentParams: {
					isSummarizeRowsView: isSummarizeRowsView,
					onInputChange: summarySelectionChanged,
					selectedColumn: {
						field: columnField,
						fieldValueType: column.fieldValueType,
					},
					columnSort,
					allSorts: getGridSorts(),
					handleSort: handleSort,
					availableSummaryFunctions,
					summaryFunction: currentlySelectedSummaryFunction,
				},
				suppressMovable: true,
				lockPinned: true,
				lockPosition: "left",
				valueGetter: (params) => {
					const { data, colDef } = params;

					const fieldTypesWithNestedNameValue = [
						"text",
						"reviewCycleStatus",
						"reviewFormStatus",
						"date",
						"groupedquestion",
						"numeric",
						"monetary",
						"salary",
					];
					const path = [...colDef.fieldPath];

					// if the column is not a nested name value, remove the last item from the path
					if (!fieldTypesWithNestedNameValue.includes(column.fieldValueType || "")) {
						path.pop();
					}
					let summarizeByValue = get(data, path, "");

					const titleCaseTypes = ["text", "reviewCycleStatus", "reviewFormStatus", "groupedquestion"];
					if (typeof summarizeByValue === "string" && titleCaseTypes.includes(column.fieldValueType || "")) {
						summarizeByValue = summarizeByValue
							?.split(/\s+|_|-/)
							?.map((word) => word?.charAt(0).toUpperCase() + word?.slice(1).toLowerCase())
							?.join(" ");
					}

					if (data.summarizeBy) {
						return summarizeByValue;
					}
					return "";
				},
			};
		});
	}

	// this canViewSummary check ensures that we don't load the not summarized view first before loading the summarized view
	const allColumns =
		canViewSummary !== undefined
			? [...(isSummarizeRowsView && canViewSummary ? summarizeRowColumns : []), ...augmentedGridColumns]
			: [];

	/**
	 * Handle toggling an enabled grid column
	 */
	const handleToggle = async (value, toggleType, isDeselecting?) => {
		const toggle = async () => {
			// handle check all columns
			if (toggleType === "all") {
				// results from column picker search input
				const resultsCheckedFields = value.map((field) => field.id);

				// Uncheck all columns
				if (isDeselecting) {
					for (const field of resultsCheckedFields) {
						toggleGridColumn(field);
					}
				}
				// Select all unselected cols
				else {
					const newlyEnabledFields = resultsCheckedFields.filter(
						(columnField) => !enabledGridColumns.includes(columnField)
					);
					for (const field of newlyEnabledFields) {
						toggleGridColumn(field);
					}
				}
			}
			// Section toggled
			else if (toggleType === "section") {
				const resultsCheckedFields = value.map((field) => field.id);
				if (isDeselecting) {
					for (const field of resultsCheckedFields) {
						toggleGridColumn(field);
					}
				} else {
					const newlyEnabledFields = resultsCheckedFields.filter(
						(columnField) => !enabledGridColumns.includes(columnField)
					);
					for (const field of newlyEnabledFields) {
						toggleGridColumn(field);
					}
				}
			}
			//Single column toggled
			else {
				toggleGridColumn(value.id);
			}
		};

		await toggle();

		// if in summarize view, hide duplicate columns and columns with count
		// depending on showAllAggregationColumns state
		if (isSummarizeRowsView && canViewSummary && gridRef.current?.api) {
			recalculateColumnVisibility(gridRef.current.api, getSummaryColumnMetadata);
		}
	};

	/**
	 * Resync the column order with the store.
	 */
	const handleColumnDataResync = () => {
		const currentColState = gridRef.current ? gridRef.current.api.getAllGridColumns() : [];
		// make sure the column is an enabled column and not a summarize field column
		const resyncedEnabledColumns = currentColState
			.map((col) => {
				const isEnabledField =
					!isSummarizeRowsView ||
					canViewSummary === false ||
					!col.getUserProvidedColDef()?.field?.startsWith("summary");

				if (isEnabledField) {
					return col.getColId();
				}
				return null;
			})
			.filter((col): col is string => col !== null);

		setEnabledGridColumns(resyncedEnabledColumns);
	};

	const handleColumnResize = (params) => {
		const currentColState = params.api.getAllGridColumns();
		const existingMeta = columnMetadata;
		setColumnMetaData(
			currentColState.reduce((acc, col: Column) => {
				const colId = col.getColId();
				acc[colId] = {
					...existingMeta[colId],
					width: col.getActualWidth(),
				};
				return acc;
			}, {})
		);
	};

	const handleCellClicked = (params) => {
		if (expandedSuperRowCell) {
			// we keep focus on the expanded super row cell if one is expanded
			const columns = params.api.getAllGridColumns();
			const column = columns.find((col) => col.instanceId === expandedSuperRowCell.col);
			params.api.setFocusedCell(expandedSuperRowCell.row, column, null);
		}
	};

	const handleToggleSectionItems = (items: DataValue[], isDeselecting: boolean) => {
		handleToggle(items, "section", isDeselecting);
	};

	/**
	 * Handle selecting an option from the grouped question options drawer
	 */
	const handleOptionSelection = (selectedObject, groupID) => {
		const cleanedGroupIDParts = groupID.split("_");
		const selectedQuestionGroupID = cleanedGroupIDParts[1];
		if (selectedObject.id === "ungroup") {
			// The incoming group id looks like "groupedquestion_629a676d98583c69d716da3c" so we want the ID only.
			if (selectedQuestionGroupID) {
				ungroupGroupedQuestion(selectedQuestionGroupID);
			}
		} else if (selectedObject.id === "edit-group") {
			loadQuestionGroup(selectedQuestionGroupID);
			setActiveModal("QUESTION_GROUP");
		}
	};
	// For the grouped fields, add a button to create new groups and add an
	// options drawer to each question.
	const columnDataSets = getAvailableGridColumns().map((group) => {
		if (group.id === "GROUPED_QUESTION") {
			return {
				...group,
				values: group.values.map((item) => {
					return {
						...item,
						action: (
							<OptionsDrawer
								options={[
									{
										id: "edit-group",
										label: "Edit",
									},
									{
										id: "ungroup",
										label: "Delete",
									},
								]}
								openOn="bottom"
								onSelect={(selectedObject) => handleOptionSelection(selectedObject, item.id)}
							/>
						),
					};
				}),
				action: (
					<Button size="micro" onClick={() => setActiveModal("QUESTION_GROUP")}>
						Create
					</Button>
				),
			};
		}
		return group;
	});
	const stillLoading = (gridData?.length === 0 && isFetching) || canViewSummary === undefined;
	const countCellRenderer = determineCellRenderer(report?.type || "");

	const TableView = (
		<div ref={gridContainerRef} style={{ height: "100%", width: "100%" }}>
			<AgGridReact<any>
				ref={gridRef}
				theme={ReportTableTheme}
				onGridReady={onGridReady}
				context={{ companyTimezone }}
				defaultColDef={{
					comparator: () => 0, // this removes the default sorting logic, but allows us to keep the sorting UI.
				}}
				rowData={gridData}
				columnDefs={allColumns}
				loading={stillLoading}
				onColumnMoved={handleColumnDataResync}
				onColumnHeaderClicked={() => {}}
				onColumnResized={handleColumnResize}
				onCellClicked={handleCellClicked}
				enableBrowserTooltips={true}
				suppressDragLeaveHidesColumns={true}
				suppressRowTransform={true}
				suppressPropertyNamesCheck={true}
				rowBuffer={100}
				components={{
					personCellRenderer,
					reviewCycleCellRenderer,
					reviewTemplateCellRenderer,
					peopleCellRenderer,
					dateCellRenderer,
					groupCellRenderer,
					booleanCellRenderer,
					reviewReportStatusCellRenderer,
					reviewCycleStatusCellRenderer,
					percentageCellRenderer,
					countCellRenderer,
					numericCellRenderer,
					superRowCellRenderer,
					superRow,
					goalCategoriesCellRenderer,
					goalOutlookCellRenderer,
					currentGoalProgressCellRenderer,
					goalTargetCellRenderer,
					goalStatusCellRenderer,
					goalTransparencyCellRenderer,
					goalCellRenderer,
					goalNameCellRenderer,
					reviewFormNameCellRenderer,
					surveyNameCellRenderer,
					SummaryFunction: SelectSummaryFunction,
					ColumnHeader,
				}}
				noRowsOverlayComponent={CustomNoRowsOverlay}
				noRowsOverlayComponentParams={{
					primaryMessage: "No results found",
					secondaryMessage: "Please try again.",
				}}
				gridOptions={{
					rowHeight: 40,
					suppressRowHoverHighlight: true,
					processUnpinnedColumns,
				}}
				// super row properties
				fullWidthCellRenderer="superRow"
				isFullWidthRow={(params) => params.rowNode.data.superRow}
				getRowHeight={superRowConfig.getRowHeight}
			/>
		</div>
	);

	chartMap[Chart.Table] = TableView;

	const visualizationReportTypes = ["reviewReport", "surveyReport", "goalReport", "employeeReport"];
	const reportingVisualizationEnabled =
		(visualizationReportTypes.includes(report?.type || "") &&
			companyReportingVisualizationFunction === "enabled" &&
			ffReportingVisualization) ||
		(report?.type === "employeeReport" &&
			companyReportingVisualizationFunction === "enabled" &&
			ffReportingVisualization);

	return (
		<div
			className={css.gridContainer}
			style={permissionToViewSharedWithReports && !permissionToCreateReviewReport ? { width: "100%" } : {}}
			data-testid="report-table"
		>
			<GridWithToolPanel
				panelInitialWidth={315}
				initialOpenPanel={openColumnPicker ? "columns" : undefined}
				menuOptions={
					adminPermissions
						? [
								{
									id: "columns",
									label: "Select Columns",
									icon: <FontAwesomeIcon icon={faTableColumns} />,
									content: (
										<ColumnPicker
											checkedItems={enabledGridColumns}
											onToggleAllItems={(val, isDeselecting) =>
												handleToggle(val, "all", isDeselecting)
											}
											onToggleSectionItems={(items, isDeselecting: boolean) =>
												handleToggleSectionItems(items, isDeselecting)
											}
											onToggleItem={(val) => handleToggle(val, "single")}
											dataSets={columnDataSets}
											// if columnDataSets has only one group, expand it by default
											initialExpandedSections={
												columnDataSets.length === 1 ? [columnDataSets[0].id] : []
											}
										/>
									),
								},
								{
									id: "summarize",
									label: "Summarize Rows",
									icon: <FontAwesomeIcon icon={faClipboardList} />,
									content: (
										<SummarizeRows updatePagination={updatePagination} handleSort={handleSort} />
									),
								},
								...(reportingVisualizationEnabled
									? [
											{
												id: "Visualize",
												label: "Visualize",
												icon: <FontAwesomeIcon icon={faChartScatterBubble} />,
												content: <VisualizePanel />,
											},
										]
									: []),
							]
						: []
				}
			>
				{chartMap[selectedChart]}
			</GridWithToolPanel>
		</div>
	);
};

export default ReportTable;
